import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import { Autocomplete, TextField, Button } from "@mui/material";
import { esES } from "@mui/x-data-grid/locales";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { getInvoices, showInvoice } from "../../controllers/api_invoices";
import { getWithholdings } from "../../controllers/apiConfig";
import { createWithholding, showWithholding } from "../../controllers/apiWithholdings";
import { showProfileClient } from "../../controllers/apiProfile";
import { getCatalogosSeniat } from "../../controllers/apiSeniat"; // Cat 17 (Concepto ISLR)
import { decryptText } from "../../controllers/api";
import { preventEnterSubmit, FORM_FOOTER_CONTAINER, FORM_BTN_CANCEL, FORM_BTN_SUBMIT } from "../../utils/formHelpers";

// ******************************************************************
// COMPONENTE EDITOR DE RETENCIÓN EN DATAGRID
// ******************************************************************
function WithholdingEditCell({ params, withholdings, onChange }) {
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <Autocomplete
      options={withholdings || []}
      fullWidth
      size="small"
      getOptionLabel={(option) =>
        option.descripcion && option.porcentaje
          ? `${option.descripcion} (${option.porcentaje}%)`
          : option.descripcion || ""
      }
      value={withholdings.find((w) => w.id === params.row.retencion_id) || null}
      onChange={(e, newValue) => onChange(params.row.id, newValue)}
      renderInput={(paramsInput) => (
        <TextField
          {...paramsInput}
          inputRef={inputRef}
          variant="standard"
          placeholder="Seleccione..."
          fullWidth
        />
      )}
    />
  );
}

// ******************************************************************
// FORM PRINCIPAL
// ******************************************************************
function FormWithholdings() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const withholding_id = queryParams.get("id");

  const apiRef = useGridApiRef();

  const [withholdings, setWithholdings] = useState([]);
  const [filterResults, setFilterResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [searchControl, setSearchControl] = useState("");
  const [errors, setErrors] = useState({}); // Estado para errores de validación
  // (Cat 17 / Concepto ISLR, 2026-07-03) Opciones del Catálogo 17 y el
  // concepto ISLR seleccionado. Es OPCIONAL: sólo aplica a retenciones ISLR
  // y no bloquea el submit si no se elige.
  // IMPORTANTE: el endpoint GET /api/seniat/catalogos?numero=17 (mapper f17
  // en seniat_routes.py) SÓLO emite {codigo, descripcion} por fila; NUNCA
  // envía 'id'. Por eso guardamos el CÓDIGO ('1'..'10') de la fila elegida y
  // lo mandamos como `concepto_islr`, que el backend (retencion_routes.py)
  // resuelve a id por codigo. Enviar concepto_islr_id sería siempre null.
  const [catalogo17, setCatalogo17] = useState([]);
  const [conceptoIslr, setConceptoIslr] = useState(null);
  const authData = JSON.parse(localStorage.getItem("authData") || "{}");
  const authclientId = authData.empresa_id;

  const [withholding, setWithholding] = useState({
    id: null,
    sujeto_retenido_rif: "",
    sujeto_retenido_nombre: "",
    numero_comprobante: "",
    periodo_fiscal: "",
    fecha_emision: "",
    fecha_entrega: "",
    items: [],
  });

  const today = new Date().toISOString().split("T")[0];


  // **********************************************************
  // CARGAR DATOS CLIENTE
  // **********************************************************
  useEffect(() => {
    if (!authclientId) return;

    showProfileClient(authclientId)
      .then((client) => {
        setWithholding((prev) => ({
          ...prev,
          sujeto_retenido_rif: client.rif || "",
          sujeto_retenido_nombre: client.nombre_empresa || "",
        }));
      })
      .catch((err) => console.error(err));
  }, [authclientId]);

  // **********************************************************
  // CARGAR RETENCIONES + RETENCIÓN EXISTENTE
  // **********************************************************
  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getWithholdings();
        setWithholdings(data);

        if (withholding_id) {
          const dataWithholding = await showWithholding(
            decryptText(withholding_id)
          );
          setWithholding(dataWithholding);
        }
      } catch {
        toast.error("Error cargando datos");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [withholding_id]);

  // **********************************************************
  // CARGAR CATÁLOGO 17 (CONCEPTO ISLR)
  // (2026-07-03) Se puebla con { id, codigo '1'..'10', descripcion } del
  // endpoint existente GET /api/seniat/catalogos?numero=17. No bloqueante:
  // si el catálogo no está disponible, el selector queda vacío y el campo
  // (opcional) simplemente no se envía.
  // **********************************************************
  useEffect(() => {
    getCatalogosSeniat({ numero: 17 })
      .then((cat) => {
        const catalogo = cat && Array.isArray(cat.catalogos)
          ? cat.catalogos.find((c) => Number(c.numero) === 17)
          : null;
        const valores = (catalogo && Array.isArray(catalogo.valores))
          ? catalogo.valores
          : [];
        // El Cat 17 llega como { codigo, descripcion } (sin id). Se conserva
        // el codigo como clave estable de cada opción; el id se resuelve en
        // el backend a partir del codigo.
        const opciones = valores.map((v) => ({
          codigo: v.codigo,
          descripcion: v.descripcion,
        }));
        setCatalogo17(opciones);
      })
      .catch((e) => {
        // No bloqueante: el concepto ISLR es opcional.
        console.warn("No se pudo cargar el Catálogo 17 (concepto ISLR).", e);
        setCatalogo17([]);
      });
  }, []);

  // **********************************************************
  // FECHA EMISIÓN
  // **********************************************************
  const handleFechaEmisionChange = (value) => {
    const periodo = value.slice(0, 7);
    setWithholding((prev) => ({
      ...prev,
      fecha_emision: value,
      periodo_fiscal: periodo,
    }));
  };
  const handleFechaEntregaChange = (value) => {
    setWithholding((prev) => ({
      ...prev,
      fecha_entrega: value,
    }));
  };

  // **********************************************************
  // BUSCAR FACTURAS
  // **********************************************************
  const handleSearchChange = async (value) => {
    setSearchControl(value);
    if (value.length < 3) return;

    try {
      const response = await getInvoices({
        tipo_documento: "FC",
        numero_control: value,
        estatus: 'normal',
      });
      setFilterResults(Array.isArray(response.data) ? response.data : []);
    } catch {
      setFilterResults([]);
    }
  };

  // **********************************************************
  // SELECCIONAR FACTURA
  // **********************************************************
  const handleInvoiceSelect = async (invoice) => {
    if (!invoice?.id) return;

    try {
      const fullInvoice = await showInvoice(invoice.id);
      const items = Array.isArray(fullInvoice.items) ? fullInvoice.items : [];

      // Agrupar items por categoría de IVA
      const groupedByIva = items.reduce((acc, item) => {
        const ivaKey = item.iva_categoria?.tasa_porcentaje || "0";
        if (!acc[ivaKey]) acc[ivaKey] = [];
        acc[ivaKey].push(item);
        return acc;
      }, {});

      // Crear una línea por cada categoría de IVA
      const newItems = Object.entries(groupedByIva).map(([iva, itemsGroup]) => {
        const baseImponible = itemsGroup.reduce((sum, item) => {
          const precio = parseFloat((item.precio_unitario || 0).toString().replace(",", "."));
          const cantidad = parseFloat((item.cantidad || 0).toString().replace(",", "."));
          return sum + precio * cantidad;
        }, 0);
        const impuesto = itemsGroup.reduce((sum, item) => {
          const precio = parseFloat((item.precio_unitario || 0).toString().replace(",", "."));
          const alicuota = parseFloat((item.iva_categoria?.tasa_porcentaje || 0).toString().replace(",", "."));
          const cantidad = parseFloat((item.cantidad || 0).toString().replace(",", "."));
          return sum + ((precio * (1 + (alicuota/100))) - precio) * cantidad;
        }, 0);
        return {
          id: Date.now() + Math.random(), // id único
          comprobante_id: fullInvoice.id,
          tipo_documento_afectado: fullInvoice.tipo_documento || "FC",
          factura_afectada_numero: (fullInvoice.numero_documento || fullInvoice.numero_factura) || "",
          factura_afectada_control: fullInvoice.numero_control || "",
          factura_afectada_fecha: fullInvoice.fecha_emision || "",
          monto_documento: parseFloat((fullInvoice.total_neto || 0).toString().replace(",", ".")) || 0,
          monto_base_imponible: parseFloat(baseImponible.toFixed(2)) || 0,
          monto_impuesto: parseFloat(impuesto.toFixed(2)) || 0,
          iva: parseFloat(iva),
          iva_categoria_id: itemsGroup[0].iva_categoria_id,
          retencion_id: null,
          tipo_retencion: null,
          monto_retenido: 0,
          // (2026-07-19 R3-A D3) montoExento RET-IVA (opcional, default 0). Porción
          // exenta del documento retenido; el usuario puede editarla por línea.
          monto_exento: 0,
        };
      });

      // (2026-05-15) Si la factura es por cuenta de un tercero, el SUJETO
      // RETENIDO es el cliente-tercero (no el cliente del emisor). Se toman
      // RIF y razón social del tercero anidado en la factura.
      const esFacturaTercero = !!(fullInvoice.doc_tercero && fullInvoice.tercero);
      const sujRif = esFacturaTercero
        ? (fullInvoice.tercero.rif || "")
        : null;
      const sujNombre = esFacturaTercero
        ? (fullInvoice.tercero.nombre_empresa || fullInvoice.tercero.denominacion_comercial || "")
        : null;

      setWithholding((prev) => ({
        ...prev,
        // (2026-05-15) La retención hereda doc_tercero/tercero_id de la
        // factura afectada: si la factura fue emitida por cuenta de un
        // tercero, la retención es "de tercero" y así se podrá filtrar en
        // el Libro de Ventas (propias vs tercero).
        doc_tercero: !!fullInvoice.doc_tercero,
        tercero_id: fullInvoice.tercero_id || null,
        // Sujeto retenido = tercero cuando la factura es de tercero; si no,
        // se conserva el del cliente del emisor (cargado por defecto).
        sujeto_retenido_rif: esFacturaTercero ? sujRif : prev.sujeto_retenido_rif,
        sujeto_retenido_nombre: esFacturaTercero ? sujNombre : prev.sujeto_retenido_nombre,
        items: [...prev.items, ...newItems],
      }));

      setSearchControl("");
      setFilterResults([]);
    } catch {
      toast.error("Error al cargar la factura");
    }
  };

  // **********************************************************
  // CAMBIO DE RETENCIÓN
  // **********************************************************
  let monto_retenido = 0;

  const handleRetencionChange = (itemId, retencion) => {
    if (!retencion) return;

    // Buscar el item correspondiente
    const item = withholding.items.find((i) => i.id === itemId);
    if (!item) return;

    let monto_retenido = 0;

    const isIvaRetention =
      retencion.codigo_seniat === "IVA_100" ||
      retencion.codigo_seniat === "IVA_75";

    if (isIvaRetention) {
      // calcular el IVA real del item
      const ivaCalculado =
        item.monto_base_imponible * (parseFloat(item.iva) / 100);

      monto_retenido = parseFloat(
        ((ivaCalculado * parseFloat(retencion.porcentaje)) / 100).toFixed(2)
      );
    } else {
      // retención sobre base imponible
      monto_retenido = parseFloat(
        ((item.monto_base_imponible * parseFloat(retencion.porcentaje)) / 100).toFixed(2)
      );
    }

    // actualizar el item
    setWithholding((prev) => ({
      ...prev,
      items: prev.items.map((i) =>
        i.id === itemId
          ? {
              ...i,
              retencion_id: retencion.id,
              tipo_retencion: retencion,
              monto_retenido: monto_retenido,
            }
          : i
      ),
    }));
  };


  // **********************************************************
  // ELIMINAR FILA
  // **********************************************************
  const handleDeleteRow = (id) => {
    setWithholding((prev) => ({
      ...prev,
      items: prev.items.filter((item) => item.id !== id),
    }));
  };

  // **********************************************************
  // MONTO EXENTO POR LÍNEA (2026-07-19 R3-A D3, opcional, >= 0)
  // **********************************************************
  const handleMontoExentoChange = (itemId, value) => {
    const v = parseFloat(String(value).replace(",", "."));
    setWithholding((prev) => ({
      ...prev,
      items: prev.items.map((i) =>
        i.id === itemId
          ? { ...i, monto_exento: isNaN(v) || v < 0 ? 0 : v }
          : i
      ),
    }));
  };

  // **********************************************************
  // COLUMNAS DATAGRID
  // **********************************************************
  const columns = [
    { field: "factura_afectada_numero", headerName: "Nro. Factura", flex: 1, headerAlign: "center", },
    { field: "factura_afectada_control", headerName: "Nro. Control", flex: 1, headerAlign: "center", },
    {
      field: "factura_afectada_fecha",
      headerName: "Fecha",
      flex: 1,
      width: 120,
      maxWidth: 120,
      headerAlign: "center",
      renderCell: (params) =>
        params.row.factura_afectada_fecha?.includes("T")
          ? params.row.factura_afectada_fecha.split("T")[0]
          : params.row.factura_afectada_fecha,
    },
    {
      field: "monto_documento",
      headerName: "Total Neto",
      flex: 1,
      headerAlign: "center",
      type: "number",
      renderCell: (params) =>
        Number(params.row.monto_documento ?? 0).toLocaleString("es-VE", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
    },
    {
      field: "monto_base_imponible",
      headerName: "Base Imponible",
      flex: 1,
      headerAlign: "center",
      type: "number",
      renderCell: (params) =>
        Number(params.row.monto_base_imponible ?? 0).toLocaleString("es-VE", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
    },
    {
      field: "iva",
      headerName: "IVA %",
      flex: 1,
      headerAlign: "center",
      type: "number",
      renderCell: (params) => `${params.row.iva || 0}`,
    },
    {
      field: "iva_categoria_id",
      headerName: "iva_categoria_id",
      flex: 1,
      renderCell: (params) => `${params.row.iva_categoria_id || 0}`,
    },
    {
      field: "monto_impuesto",
      headerName: "Impuesto",
      flex: 1,
      headerAlign: "center",
      type: "number",
      renderCell: (params) =>
        Number(params.row.monto_impuesto ?? 0).toLocaleString("es-VE", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
    },
    {
      field: "retencion_id",
      headerName: "Tipo Retención",
      width: 250,
      editable: true,
      headerAlign: "center",
      renderCell: (params) => params.row.tipo_retencion?.descripcion || "—",
      renderEditCell: (params) => (
        <WithholdingEditCell
          params={params}
          withholdings={withholdings}
          onChange={handleRetencionChange}
        />
      ),
    },
    {
      field: "monto_retenido",
      headerName: "Monto Retenido",
      flex: 1,
      headerAlign: "center",
      type: "number",
      renderCell: (params) =>
        Number(params.row.monto_retenido ?? 0).toLocaleString("es-VE", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
    },
    {
      // (2026-07-19 R3-A D3) Monto Exento por línea — OPCIONAL, numérico >= 0.
      // Input en línea (auto-contenido; no usa la maquinaria de edición del grid).
      field: "monto_exento",
      headerName: "Monto Exento",
      flex: 1,
      headerAlign: "center",
      sortable: false,
      renderCell: (params) => (
        <input
          type="number"
          min="0"
          step="0.01"
          value={params.row.monto_exento ?? 0}
          onChange={(e) => handleMontoExentoChange(params.row.id, e.target.value)}
          className="border rounded px-2 py-1 w-24 text-right text-xs"
        />
      ),
    },
    {
      field: "acciones",
      headerName: "",
      flex: 0.4,
      headerAlign: "center",
      sortable: false,
      renderCell: (params) => (
        <Button color="error" size="small" onClick={() => handleDeleteRow(params.row.id)}>
          <i className="fa-solid fa-lg fa-trash"></i>
        </Button>
      ),
    },
  ];

  // Validación de campos requeridos
  const validate = () => {
    const newErrors = {};
    var errorToast = [];
    // (2026-07-18 A8) numero_comprobante es OPCIONAL (espejo del backend híbrido):
    // si se deja vacío, el backend lo genera AAAAMM+correlativo. Si el usuario
    // escribe algo, debe ser EXACTAMENTE 14 dígitos numéricos (AAAAMM + correlativo
    // de 8) — misma regla/mensaje que el backend (retencion_routes: ^\d{14}$, 400).
    if (withholding.numero_comprobante && !/^\d{14}$/.test(String(withholding.numero_comprobante).trim())){
      newErrors.numero_comprobante = "El número de comprobante, si se ingresa, debe ser exactamente 14 dígitos numéricos (AAAAMM + correlativo de 8)";
      errorToast.push("- El número de comprobante, si se ingresa, debe ser exactamente 14 dígitos numéricos (AAAAMM + correlativo de 8)");
    }
    if (!withholding.fecha_emision){
      newErrors.fecha_emision = "La fecha de emisión es obligatoria";
      errorToast.push("- La fecha de emisión es obligatoria");
    }
    if (!withholding.fecha_entrega){
      newErrors.fecha_entrega = "La fecha de entrega es obligatoria";
      errorToast.push("- La fecha de entrega es obligatoria");
    }
    if (!withholding.periodo_fiscal){
      newErrors.periodo_fiscal = "El período fiscal es obligatorio";
      errorToast.push("- El período fiscal es obligatorio");
    }
    if (!withholding.items.length){
      if (newErrors.items == undefined){
        newErrors.items = [];
      }
      newErrors.items.push("Debe agregar al menos una factura");
      errorToast.push("- Debe agregar al menos una factura");
    }
    for (let item of withholding.items) {
      if (!item.retencion_id){
        if (newErrors.items == undefined){
          newErrors.items = [];
        }
        newErrors.items.push(`Debe seleccionar el tipo de retención para la factura ${item.factura_afectada_numero}`);
        errorToast.push(`- Debe seleccionar el tipo de retención para la factura ${item.factura_afectada_numero}`);
      }
      // (2026-07-19 R3-A D3) montoExento OPCIONAL — espejo del backend
      // (_to_decimal(monto_exento), default 0): si viene, debe ser numérico >= 0.
      if (item.monto_exento !== undefined && item.monto_exento !== null && String(item.monto_exento).trim() !== "") {
        const me = parseFloat(String(item.monto_exento).replace(",", "."));
        if (isNaN(me) || me < 0) {
          if (newErrors.items == undefined){
            newErrors.items = [];
          }
          newErrors.items.push(`El monto exento de la factura ${item.factura_afectada_numero} debe ser numérico y mayor o igual a 0`);
          errorToast.push(`- El monto exento de la factura ${item.factura_afectada_numero} debe ser numérico y mayor o igual a 0`);
        }
      }
    }
    setErrors(newErrors);
    if (errorToast.length > 0){
      toast.error(<div>
        {errorToast.map(item => (
            <span className="text-start">{item}<br/></span>
          ))}
      </div>)
      setButtonDisabled(false);
    }
    return Object.keys(newErrors).length === 0;
  };

  // **********************************************************
  // SUBMIT CON VALIDACIONES
  // **********************************************************
  const handleSubmit = async (e) => {
    e.preventDefault();
    setButtonDisabled(true);
    if (!validate()) return;

    // ---------------------------------------------------------
    // Construir JSON final a enviar (payload)
    // ---------------------------------------------------------
    // (Cat 17 / Concepto ISLR, 2026-07-03) Sólo se envía para retenciones
    // ISLR (alguna línea con tipo de retención NO IVA). Es OPCIONAL: si no
    // hay concepto elegido o la retención es IVA, se envía null.
    const esISLR = withholding.items.some((item) => {
      const cod = item.tipo_retencion?.codigo_seniat;
      return cod && cod !== "IVA_100" && cod !== "IVA_75";
    });

    const payload = {
      sujeto_retenido_rif: withholding.sujeto_retenido_rif,
      sujeto_retenido_nombre: withholding.sujeto_retenido_nombre,
      numero_comprobante: withholding.numero_comprobante,
      periodo_fiscal: withholding.periodo_fiscal,
      fecha_emision: withholding.fecha_emision,
      fecha_entrega: withholding.fecha_entrega,
      // Concepto ISLR de cabecera (Cat 17). Se envía el CÓDIGO ('1'..'10')
      // vía `concepto_islr`; el backend lo resuelve al id de la fila. El
      // endpoint del catálogo no expone id, por eso no se manda
      // concepto_islr_id (sería siempre null). Sólo ISLR; opcional.
      concepto_islr: esISLR ? (conceptoIslr || null) : null,
      // (2026-05-15) Marca de operación por cuenta de tercero, heredada de
      // la factura afectada. El backend la persiste en comprobantes_retencion.
      doc_tercero: !!withholding.doc_tercero,
      tercero_id: withholding.tercero_id || null,
      items: withholding.items.map((item) => ({
        factura_afectada_numero: item.factura_afectada_numero,
        factura_afectada_control: item.factura_afectada_control,
        factura_afectada_fecha: item.factura_afectada_fecha?.split("T")[0] || "",
        tipo_documento_afectado: item.tipo_documento_afectado,
        monto_documento: item.monto_documento.toFixed(2),
        monto_base_imponible: item.monto_base_imponible.toFixed(2),
        retencion_id: item.retencion_id,
        iva_categoria_id: item.iva_categoria_id,
        monto_retenido: item.monto_retenido.toFixed(2),
        // (2026-07-19 R3-A D3) montoExento RET-IVA (opcional, default 0).
        monto_exento: (Number(item.monto_exento) || 0).toFixed(2),
      })),
    };

    console.log("Payload enviado al backend:");
    console.log(JSON.stringify(payload, null, 2));

    try {
      if (!withholding.id) {
        await createWithholding(payload);
        toast.success("Retención creada correctamente", {
          onClose: () => navigate("/withholdings"),
        });
      }
    } catch (err) {
      console.error("Error del servidor:", err);

      const backendMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.response?.data?.detail ||
        "Error al guardar";

      toast.error(backendMessage);
    } finally {
      setButtonDisabled(false);
    }
  };

  if (loading) return <div>Cargando...</div>;

  // **********************************************************
  // MODO ISLR (2026-07-03)
  // El tipo de retención es por línea: IVA usa codigo_seniat 'IVA_100'/'IVA_75';
  // cualquier otro código corresponde a ISLR. La retención es ISLR cuando al
  // menos una línea tiene un tipo de retención seleccionado que NO es IVA.
  // El selector de Concepto ISLR (Cat 17) sólo se muestra en ese caso.
  // **********************************************************
  const esISLR = withholding.items.some((item) => {
    const cod = item.tipo_retencion?.codigo_seniat;
    return cod && cod !== "IVA_100" && cod !== "IVA_75";
  });

  // **********************************************************
  // RENDER
  // **********************************************************
  return (
    <div className="mx-auto w-full">
      <ToastContainer />
      <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-lg bg-blueGray-100 border-0">
        <div className="rounded-t bg-white mb-0 px-6 py-6 flex justify-between">
          <h6 className="text-blueGray-700 text-xl font-bold">
            {!withholding.id ? "Crear" : "Actualizar"} Retención
          </h6>
        </div>

        <div className="flex-auto px-4 lg:px-10 py-10 pt-0 bg-white">
          <form onSubmit={handleSubmit} onKeyDown={preventEnterSubmit}>
            <h4 className="font-bold mb-2">Datos del Sujeto Retenido</h4>
            <hr className="my-6 border-b-1 border-blueGray-300" />

            <div className="flex flex-wrap">
              <div className="w-full lg:w-3/12 px-4">
                <label className="block text-blueGray-600 text-xs font-bold mb-2">RIF</label>
                <input 
                  disabled
                  className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-gray-200 rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                  value={withholding.sujeto_retenido_rif || ""} />
              </div>
              <div className="w-full lg:w-5/12 px-4">
                <label className="block text-blueGray-600 text-xs font-bold mb-2">Razón Social</label>
                <input 
                  disabled 
                  className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-gray-200 rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                  value={withholding.sujeto_retenido_nombre || ""} />
              </div>
              <div className="w-full lg:w-3/12 px-4">
                <label className="block text-blueGray-600 text-xs font-bold mb-2">Nro. Comprobante (opcional)</label>
                <input
                  type="text"
                  placeholder="Automático (AAAAMM+correlativo)"
                  className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                  value={withholding.numero_comprobante || ""}
                  onChange={(e) =>
                    setWithholding((prev) => ({ ...prev, numero_comprobante: e.target.value }))
                  }
                />
                {/* (2026-07-18 A8) Campo OPCIONAL: si se deja vacío, el backend lo
                    genera AAAAMM + correlativo de 8 dígitos; si se ingresa, deben
                    ser exactamente 14 dígitos numéricos. */}
                <p className="text-blueGray-400 text-xs mt-1">Opcional. Si se deja vacío se genera automáticamente (AAAAMM + correlativo de 8 dígitos).</p>
                {errors.numero_comprobante && <p className="text-red-500 text-xs mt-1">{errors.numero_comprobante}</p>}
              </div>
            </div>

            <div className="flex flex-wrap mt-4">
              <div className="w-full lg:w-3/12 px-4">
                <label className="block text-blueGray-600 text-xs font-bold mb-2">Fecha Emisión</label>
                <input
                  type="date"
                  className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                  value={withholding.fecha_emision || ""}
                  max={today}
                  onChange={(e) => handleFechaEmisionChange(e.target.value)}
                />
                {errors.fecha_emision && <p className="text-red-500 text-xs mt-1">{errors.fecha_emision}</p>}
              </div>

              <div className="w-full lg:w-3/12 px-4">
                <label className="block text-blueGray-600 text-xs font-bold mb-2">Fecha Entrega</label>
                <input
                  type="date"
                  className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                  value={withholding.fecha_entrega || ""}
                  max={today}
                  onChange={(e) => handleFechaEntregaChange(e.target.value)}
                />
                {errors.fecha_entrega && <p className="text-red-500 text-xs mt-1">{errors.fecha_entrega}</p>}
              </div>

              <div className="w-full lg:w-3/12 px-4">
                <label className="block text-blueGray-600 text-xs font-bold mb-2">Periodo Fiscal</label>
                <input
                  disabled
                  className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-gray-200 rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                  value={withholding.periodo_fiscal || ""}
                />
                {errors.periodo_fiscal && <p className="text-red-500 text-xs mt-1">{errors.periodo_fiscal}</p>}
              </div>
            </div>

            <hr className="my-6 border-b-1 border-blueGray-300" />
            <h4 className="font-bold mb-2">Documentos Afectados</h4>

            <div className="flex flex-wrap mt-6">
              <div className="w-full lg:w-6/12 px-4">
                <Autocomplete
                  options={filterResults || []}
                  getOptionLabel={(option) =>
                    `Factura: ${option.numero_documento || option.numero_factura} - Nro. Control: ${option.numero_control}`
                  }
                  value={null}
                  onChange={(e, selectedInvoice) => handleInvoiceSelect(selectedInvoice)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Buscar factura por Nro. Control"
                      value={searchControl}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      size="small"
                      fullWidth
                    />
                  )}
                />
              </div>
            </div>

            <div className="flex flex-wrap mt-6">
              <DataGrid
                apiRef={apiRef}
                rows={withholding.items}
                columns={columns}
                getRowId={(row) => row.id}
                autoHeight
                pageSize={5}
                localeText={esES.components.MuiDataGrid.defaultProps.localeText}
                columnVisibilityModel={{
                  iva_categoria_id: false, // oculta la columna "iva_categoria_id"
                }}
                sx={{
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 'bold',   // ahora sí en negrita
                    fontSize: '0.80rem',
                  },
                }}
              />
            </div>
            <div className="w-full lg:w-12/12 px-5 my-1 text-center">
              {errors.items &&
                <p className="text-red-500 text-xs mt-1">
                  {errors.items.map(item => (
                    <span className="text-start">{item}<br/></span>
                  ))}
              </p>}
            </div>

            {/* (Cat 17 / Concepto ISLR, 2026-07-03) Selector OPCIONAL visible
                sólo para retención ISLR. Emite TipoComprobante (tabla 22) a
                nivel de cabecera; se envía concepto_islr (codigo '1'..'10' de
                la fila Cat 17), que el backend resuelve al id. */}
            {esISLR && (
              <>
                <hr className="my-6 border-b-1 border-blueGray-300" />
                <h4 className="font-bold mb-2">Concepto ISLR (opcional)</h4>
                <div className="flex flex-wrap mt-4">
                  <div className="w-full lg:w-6/12 px-4">
                    <label className="block text-blueGray-600 text-xs font-bold mb-2">
                      Concepto ISLR (Catálogo 17)
                    </label>
                    <Autocomplete
                      options={catalogo17 || []}
                      size="small"
                      getOptionLabel={(option) =>
                        option.codigo
                          ? `${option.codigo} - ${option.descripcion || ""}`
                          : option.descripcion || ""
                      }
                      isOptionEqualToValue={(option, value) => option.codigo === value.codigo}
                      value={catalogo17.find((c) => c.codigo === conceptoIslr) || null}
                      onChange={(e, newValue) =>
                        setConceptoIslr(newValue ? newValue.codigo : null)
                      }
                      renderInput={(paramsInput) => (
                        <TextField
                          {...paramsInput}
                          variant="standard"
                          placeholder="Seleccione un concepto (opcional)..."
                          fullWidth
                        />
                      )}
                    />
                  </div>
                </div>
              </>
            )}

            <div className={FORM_FOOTER_CONTAINER}>
              <button
                type="button"
                className={FORM_BTN_CANCEL}
                disabled={buttonDisabled}
                onClick={() => navigate("/withholdings")}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className={FORM_BTN_SUBMIT}
                disabled={buttonDisabled}
              >
                {buttonDisabled ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default FormWithholdings;
